import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CollaborationDataService } from '../../services/collaboration-data.service';
import { ApiService } from '../../../shared/services/api.service';
import { GetPartnerEmails } from '../../../../graphql/queries';
import { Action, GetPartnerEmailsQuery, GetPartnerEmailsQueryVariables } from '../../../../API';
import { VALID_EMAIL_PATTERN_REGEX } from '../../../shared/utils/constants';

@Component({
  selector: 'app-collaboration-invitation',
  templateUrl: './collaboration-invitation.component.html',
  styleUrl: './collaboration-invitation.component.scss',
})
export class CollaborationInvitationComponent implements OnInit {
  @Output() nextStep = new EventEmitter<void>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() cancelBtn = new EventEmitter<void>();

  invitationForm!: FormGroup;
  partnerEmails: string[] = [];
  currentEmail = '';
  partnerOrgType = '';
  isValidating = false;

  constructor(
    private fb: FormBuilder,
    private nzMessageService: NzMessageService,
    public collaborationDataService: CollaborationDataService,
    private apiService: ApiService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadExistingData();
    this.collaborationDataService.errorMessage = '';

    // Set initial validation state
    this.updateValidationState();
  }

  initializeForm(): void {
    this.invitationForm = this.fb.group({
      partnerEmail: ['', [Validators.required, Validators.pattern(VALID_EMAIL_PATTERN_REGEX)]],
      partnerInviteMessage: ['', [Validators.maxLength(1000)]],
    });
  }

  private loadExistingData(): void {
    const collaborationDetails = this.collaborationDataService.collaborationDetails;
    if (!collaborationDetails) return;

    if (collaborationDetails.partnerInviteMessage) {
      this.invitationForm.patchValue({
        partnerInviteMessage: collaborationDetails.partnerInviteMessage,
      });
    }

    if (collaborationDetails.partnerEmail && collaborationDetails.partnerEmail.length > 0) {
      this.partnerEmails = [...collaborationDetails.partnerEmail];
      this.partnerOrgType = collaborationDetails.partnerOrgType || '';

      if (this.partnerEmails.length > 0) {
        this.currentEmail = this.partnerEmails[0];
        this.invitationForm.patchValue({
          partnerEmail: this.currentEmail,
        });
        this.partnerEmailControl?.disable();
      }
    }

    // Update validation state after loading data
    this.updateValidationState();
  }

  onValidateEmail(): void {
    const email = this.partnerEmailControl?.value;
    if (email && this.partnerEmailControl?.valid) {
      this.isValidating = true;
      this.collaborationDataService.errorMessage = '';
      this.currentEmail = email;

      const variables: GetPartnerEmailsQueryVariables = {
        collaborationRequest: {
          action: Action.LIST_EMAIL,
          email: email,
        },
      };

      this.apiService
        .query<GetPartnerEmailsQuery, GetPartnerEmailsQueryVariables>(GetPartnerEmails, variables)
        .then((response) => {
          const partnerDataResponse = response.data?.GetPartnerEmails;
          if (partnerDataResponse && partnerDataResponse?.success && partnerDataResponse?.data) {
            const partnerData = partnerDataResponse.data;
            this.partnerEmails = [partnerData.email];
            this.partnerOrgType = partnerData.orgType || '';

            if (partnerData.admins && partnerData.admins.length > 0) {
              const filteredAdminEmail = partnerData.admins.filter(
                (admin) => admin?.toLowerCase() !== email?.toLowerCase(),
              );
              this.partnerEmails.push(...filteredAdminEmail);
            }

            this.isValidating = false;
            this.partnerEmailControl?.disable();
            this.collaborationDataService.errorMessage = '';
            this.updateValidationState();
            this.nzMessageService.success(partnerDataResponse?.message ?? 'Partner emails fetched successfully.');
          } else {
            this.isValidating = false;
            this.nzMessageService.error(partnerDataResponse?.message ?? 'Failed to validate email. Please try again.');
            this.collaborationDataService.errorMessage =
              partnerDataResponse?.message ?? 'Failed to validate email. Please try again.';
          }
        })
        .catch((error) => {
          this.isValidating = false;
          this.nzMessageService.error('Error validating email. Please try again. ' + error?.message);
          this.collaborationDataService.errorMessage = 'Error validating email. Please try again.';
        });
    }
  }

  onRemoveEmail(index: number): void {
    if (index === 0) {
      this.partnerEmails = [];
      this.currentEmail = '';
      this.partnerOrgType = '';
      this.partnerEmailControl?.enable();
      this.partnerEmailControl?.reset();
      this.collaborationDataService.errorMessage = '';
    } else {
      this.partnerEmails.splice(index, 1);
    }
    this.updateValidationState();
  }

  onRemovePartner(index: number): void {
    this.partnerEmails.splice(index, 1);
  }

  onNext(): void {
    if (this.partnerEmails.length === 0) {
      this.nzMessageService.warning('Please validate partner email to continue');
      return;
    }

    this.collaborationDataService.currentStep = 3;
    this.nextStep.emit();
  }

  onCancel(): void {
    this.cancelBtn.emit();
  }

  onSaveAsDraft(): void {
    this.saveInvitationData();
  }

  private saveInvitationData(): void {
    this.collaborationDataService.updateCollaborationDetails({
      partnerEmail: this.partnerEmails,
      partnerInviteMessage: this.invitationForm.get('partnerInviteMessage')?.value || '',
      partnerOrgType: this.partnerOrgType,
    });
  }

  get partnerEmailControl(): FormControl<string> {
    return this.invitationForm.get('partnerEmail') as FormControl;
  }

  isValid(): boolean {
    return this.partnerEmails.length > 0;
  }

  private updateValidationState(): void {
    this.collaborationDataService.isStep3Valid = this.isValid();
  }

  reset(): void {
    const original = this.collaborationDataService.originalCollaborationData;

    this.invitationForm.patchValue({
      partnerEmail: '',
      partnerInviteMessage: original?.partnerInviteMessage || '',
    });
    this.partnerEmails = original?.partnerEmail ? [...original.partnerEmail] : [];
    this.currentEmail = this.partnerEmails.length > 0 ? this.partnerEmails[0] : '';
    this.partnerOrgType = original?.partnerOrgType || '';
    this.collaborationDataService.errorMessage = '';

    if (this.partnerEmails.length > 0) {
      this.partnerEmailControl?.disable();
    } else {
      this.partnerEmailControl?.enable();
    }

    this.collaborationDataService.updateCollaborationDetails({
      partnerEmail: this.partnerEmails,
      partnerInviteMessage: original?.partnerInviteMessage || '',
      partnerOrgType: this.partnerOrgType,
    });
  }
}
